import { Title } from "@src/lib/ui-react";
import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import {
  CircularProgress,
  Box,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useGetAttendanceVerification } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/api/useGetAttendanceVerification";
import { hasEnabledMethodThatRequiresCredentials } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/types";
import { useUpdateWorkerCredentialConfiguration } from "@src/appV2/FacilityProfile/api/useUpdateWorkerCredentialConfiguration";
import { useToast } from "@src/appV2/lib";
import { getCredentialVariant } from "@src/appV2/WorkerCredentials/utils/getCredentialVariant";
import { useState } from "react";

import { SettingsItem } from "./SettingsItem";
import { SettingsItemTitle } from "./SettingsItemTitle";

interface WorkerCredentialConfigurationProps {
  workplace: WorkplaceProfileResponse;
  onPreferencesChanged: () => void;
}

const DEFAULT_THRESHOLD = 24;

export function WorkerCredentialConfiguration({
  workplace,
  onPreferencesChanged,
}: WorkerCredentialConfigurationProps) {
  const { attributes, id: workplaceId } = workplace.data;
  const config = attributes.workerCredentialConfiguration;

  const [chartingEnabled, setChartingEnabled] = useState(config?.charting.enabled ?? false);
  const [timekeepingEnabled, setTimekeepingEnabled] = useState(
    config?.timekeeping.enabled ?? false
  );
  const [onboardingViewEnabled, setOnboardingViewEnabled] = useState(
    config?.onboardingView.enabled ?? false
  );
  const [thresholdValue, setThresholdValue] = useState(
    String(config?.onboardingView.shiftStartingSoonThreshold.value ?? DEFAULT_THRESHOLD)
  );
  const [credentialVariant, setCredentialVariant] = useState<"pin-only" | "pin-plus-password">(
    getCredentialVariant(workplace).variant ?? "pin-only"
  );

  const { showSuccessToast, showErrorToast } = useToast();
  const { refetch: refetchAttendanceVerification } = useGetAttendanceVerification(workplaceId);
  const { isLoading: isUpdating, mutateAsync: updateConfiguration } =
    useUpdateWorkerCredentialConfiguration();

  const buildConfiguration = (
    overrides: Partial<{
      chartingEnabled: boolean;
      timekeepingEnabled: boolean;
      onboardingViewEnabled: boolean;
      thresholdValue: number;
      credentialVariant: "pin-only" | "pin-plus-password";
    }>
  ) => ({
    charting: { enabled: overrides.chartingEnabled ?? chartingEnabled },
    timekeeping: {
      enabled: overrides.timekeepingEnabled ?? timekeepingEnabled,
      credentialVariant: overrides.credentialVariant ?? credentialVariant,
    },
    onboardingView: {
      enabled: overrides.onboardingViewEnabled ?? onboardingViewEnabled,
      shiftStartingSoonThreshold: {
        value:
          overrides.thresholdValue ??
          config?.onboardingView.shiftStartingSoonThreshold.value ??
          DEFAULT_THRESHOLD,
        unit: "hours" as const,
      },
    },
  });

  const handleToggle = async (
    field: "chartingEnabled" | "timekeepingEnabled" | "onboardingViewEnabled",
    checked: boolean,
    setState: (value: boolean) => void
  ) => {
    setState(checked);
    try {
      await updateConfiguration({
        workplaceId,
        workerCredentialConfiguration: buildConfiguration({ [field]: checked }),
      });
      showSuccessToast("Worker credential configuration updated successfully");
      onPreferencesChanged();
    } catch {
      showErrorToast("Error updating worker credential configuration. Please try again later.");
      setState(!checked);
    }
  };

  return (
    <Stack position="relative" paddingLeft={2} sx={{ opacity: isUpdating ? 0.5 : 1 }}>
      <Title component="h3">Worker Credentials Configuration</Title>

      {isUpdating ? (
        <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
          <CircularProgress size={48} />
        </Box>
      ) : null}

      <SettingsItem
        isLoading={isUpdating}
        item={{
          key: "workerCredentialCharting",
          title: "Charting",
          subtitle:
            "When enabled, workers will be reminded to complete charting, and admins will be able to share and manage charting credentials on their behalf.",
          checked: chartingEnabled,
          onChange: (checked: boolean) => {
            void handleToggle("chartingEnabled", checked, setChartingEnabled);
          },
        }}
      />

      <SettingsItem
        isLoading={isUpdating}
        item={{
          key: "workerCredentialTimekeeping",
          title: "Timekeeping",
          subtitle:
            "When enabled, workplace users will be able to share and manage timekeeping credentials on behalf of workers.",
          checked: timekeepingEnabled,
          onChange: async (checked: boolean) => {
            if (!checked) {
              const { data: latestVerification } = await refetchAttendanceVerification();
              if (
                hasEnabledMethodThatRequiresCredentials(
                  latestVerification?.data.attributes.methods ?? []
                )
              ) {
                showErrorToast(
                  "Cannot disable timekeeping while there are enabled timekeeping methods. Please disable them first."
                );
                return;
              }
            }
            void handleToggle("timekeepingEnabled", checked, setTimekeepingEnabled);
          },
        }}
      />

      {timekeepingEnabled && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 3,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Stack direction="column" spacing={0}>
            <SettingsItemTitle
              title="Credential Variant"
              subtitle="Choose which credentials workers need to provide for timekeeping."
            />
          </Stack>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="credential-variant-label">Credential Variant</InputLabel>
            <Select
              labelId="credential-variant-label"
              value={credentialVariant}
              label="Credential Variant"
              disabled={isUpdating}
              onChange={async (e) => {
                const newVariant = e.target.value as "pin-only" | "pin-plus-password";
                const previousVariant = credentialVariant;
                setCredentialVariant(newVariant);
                try {
                  await updateConfiguration({
                    workplaceId,
                    workerCredentialConfiguration: buildConfiguration({
                      credentialVariant: newVariant,
                    }),
                  });
                  showSuccessToast("Credential variant updated successfully");
                  onPreferencesChanged();
                } catch {
                  showErrorToast("Error updating credential variant. Please try again later.");
                  setCredentialVariant(previousVariant);
                }
              }}
            >
              <MenuItem value="pin-only">Pin only</MenuItem>
              <MenuItem value="pin-plus-password">Pin + Password</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      <SettingsItem
        isLoading={isUpdating}
        item={{
          key: "workerCredentialOnboardingView",
          title: "Onboarding View",
          subtitle:
            "When enabled, shows professionals who are missing credentials and have upcoming shifts, so credentials can be shared before their shift starts.",
          checked: onboardingViewEnabled,
          onChange: (checked: boolean) => {
            void handleToggle("onboardingViewEnabled", checked, setOnboardingViewEnabled);
          },
        }}
      />

      {onboardingViewEnabled && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 3,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Stack direction="column" spacing={0}>
            <SettingsItemTitle
              title="Shift Starting Soon Threshold"
              subtitle="Number of hours before a shift starts to consider it 'starting soon' and highlight professionals who still need credentials."
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ minWidth: "200px" }} justifyContent="flex-end">
            <TextField
              type="number"
              value={thresholdValue}
              onChange={(e) => {
                setThresholdValue(e.target.value);
              }}
              disabled={isUpdating}
              sx={{ width: "100px" }}
              size="small"
              inputProps={{ min: 1 }}
            />
            <Button
              variant="text"
              color="primary"
              onClick={async () => {
                const value = Number(thresholdValue);
                try {
                  await updateConfiguration({
                    workplaceId,
                    workerCredentialConfiguration: buildConfiguration({ thresholdValue: value }),
                  });
                  showSuccessToast("Shift starting soon threshold updated successfully");
                  onPreferencesChanged();
                } catch {
                  showErrorToast(
                    "Error updating shift starting soon threshold. Please try again later."
                  );
                  setThresholdValue(
                    String(
                      config?.onboardingView.shiftStartingSoonThreshold.value ?? DEFAULT_THRESHOLD
                    )
                  );
                }
              }}
              disabled={isUpdating || Number(thresholdValue) < 1}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
